import { ICON__plus } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__progress_bar } from '@/components/ui/UI_PRIMITIVE__progress_bar';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_component_is_mounted_50 } from '@/hooks/is_mounted/use_component_is_mounted_50';
import { use_slice_ui, use_slice_user, use_slice_words, use_slice_workspace } from '@/hooks/store/use_slices';
import { use_toast_no_related_words } from '@/hooks/toasts/use_toast_no_related_words';
import { use_toast_related_words_created } from '@/hooks/toasts/use_toast_related_words_created';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { store } from '@/state/store';
import { local_words_set_word_for_adding_related } from '@/state/words/local_words_set_word_for_adding_related_words';
import { local_word_with_related_words_create_in_progress_add } from '@/state/words/related_words_create/local_word_with_related_words_create_in_progress_add';
import { remote_word_with_related_words_create } from '@/state/words/related_words_create/remote_word_with_related_words_create';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { RequestWordsWithRelatedWordsCreate } from '@shared/dto/+word/word_with_related_words/_requests/request_words_with_related_words_create';
import { DTOWordWithRelatedWords } from '@shared/dto/+word/word_with_related_words/dto_word_with_related_words';
import { FIELD__token } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD__token';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  field: FIELD__token;
}

//button disabled if word english is more than one word = phrase
//button disabled if user didnt receive from server data about similar words
export function G_BUTTON__token_related_words_create({ field }: Props) {
  /*
   * STORE
   */
  const { t } = useTranslation([ENUM__i18n_namespace.PAGE_GENERATION, ENUM__i18n_namespace.BASE_UI]);

  const dispatch = use_dispatch();
  const { toast, dismiss } = useToast();

  const { workspace_current } = use_slice_workspace(['workspace_current']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { words_with_related_words_create_in_progress } = use_slice_words(['words_with_related_words_create_in_progress']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */

  const [is_loading, set_is_loading] = useState(false);
  const [is_clicked, set_is_clicked] = useState(false);

  const ref_current_toast_id = useRef('');

  const { show: show_toast_no_related_words } = use_toast_no_related_words(field.word_english.get_value());
  const { show: show_toast_related_words_created } = use_toast_related_words_created(field.word_english.get_value());

  const { is_mounted } = use_component_is_mounted_50();

  const tooltip_title = t(i18n_page_generation.sidebar_user_input.button.token_add_related_words.tooltip);

  /*
   * EFFECTS
   */
  use_effect(() => {
    return () => void dispatch(local_words_set_word_for_adding_related(''));
  }, []);

  /*
   * EVENTS
   */
  function onTimerComplete() {
    set_is_clicked(false);
    dismiss(ref_current_toast_id.current);
    ref_current_toast_id.current = '';
  }

  function on_request_sent() {
    let { words_with_related_words_map } = store.getState().slice_words;

    let word_with_related_words = words_with_related_words_map.get_value_or_default(field.word_english.get_value(), DTOWordWithRelatedWords.CREATE_STUB());

    if (word_with_related_words.synonyms.is_empty() && word_with_related_words.antonyms.is_empty()) {
      show_toast_no_related_words();
    } else {
      show_toast_related_words_created();
    }
  }

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();

    if (is_clicked || !user_settings.confirm_action_image_to_text) {
      onTimerComplete();

      //show view
      dispatch(local_words_set_word_for_adding_related(field.word_english.get_value()));

      //add to list in progress
      dispatch(local_word_with_related_words_create_in_progress_add([field.word_english.get_value()]));

      let request = new RequestWordsWithRelatedWordsCreate();
      request.word_english = field.word_english.get_value();
      request.workspace_id = workspace_current._id;

      set_is_loading(true);

      await dispatch(remote_word_with_related_words_create(request));

      on_request_sent();

      set_is_loading(false);
    } else {
      set_is_clicked(true);

      let text = t(i18n_base_ui.toasts.token_add_related_words.description, {
        word: field.word_english,
        number: CONSTANTS__shared.PRICE_CREDITS_PER_WORD_GET_RELATED_WORDS.toString(),
      });

      let toast_data = toast({
        title: t(i18n_base_ui.toasts.token_add_related_words.title),
        duration: CONSTANTS__client.TIMEOUT_PURCHASE,
        description: <UI_PRIMITIVE__progress_bar timeout_ms={CONSTANTS__client.TIMEOUT_PURCHASE} {...{ onTimerComplete, text }} />,
      });

      ref_current_toast_id.current = toast_data.id;
    }
  }

  /*
   * METHODS
   */

  function is_disabled(): boolean {
    return g_general__ui_disabled || is_loading || words_with_related_words_create_in_progress.has(field.word_english.get_value());
  }

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }}>
      <UI_PRIMITIVE__button
        variant="no-border-on-component"
        size="rect-10-8"
        {...{ onClick }}
        disabled={is_disabled()}
        className={`border border-purchase p-0 h-6 hover-animation-important 
          ${is_mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
      >
        <ICON__plus className={`size-4 text-purchase`} />
      </UI_PRIMITIVE__button>
    </UI_PRIMITIVE__tooltip>
  );
}
